import React, { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Clock, Send, Loader2, CheckCircle2 } from "lucide-react";
import { CONTACT } from "@/lib/content";
import { api } from "@/lib/api";

const finp = "w-full bg-white/5 border border-white/12 rounded-lg px-4 py-3 outline-none focus:border-[#1F5EFF] text-white text-sm placeholder:text-white/25";

function ContactForm() {
  const [f, setF] = useState({ name: "", phone: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || f.phone.trim().length < 4 || !f.email.trim() || !f.message.trim()) {
      setError("Please fill in your name, phone, email and message.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await api.post("/quotes", {
        name: f.name.trim(),
        phone: f.phone.trim(),
        email: f.email.trim(),
        message: f.message.trim(),
        service: "General question",
      });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong — please call or email us instead.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="flex flex-col items-start gap-4" data-testid="contact-form-success">
        <CheckCircle2 className="w-10 h-10 text-emerald-400" strokeWidth={1.5} />
        <h3 className="font-display font-light text-3xl leading-tight">Message sent.</h3>
        <p className="text-white/60 max-w-md leading-relaxed">
          Thanks, {f.name.split(" ")[0]} — we reply to every message within 2 business hours.
          Need it sooner? Call <a className="text-[#8db2ff] hover:underline" href={`tel:${CONTACT.phone.replace(/[^\d+]/g, "")}`}>{CONTACT.phone}</a>.
        </p>
        <button
          onClick={() => { setDone(false); setF({ name: "", phone: "", email: "", message: "" }); }}
          className="text-sm text-[#8db2ff] hover:text-white transition-colors"
          data-testid="contact-form-again"
        >
          Send another message →
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-4" data-testid="contact-form">
      <div className="flex items-center gap-3">
        <span className="w-8 h-px bg-[#1F5EFF]" />
        <span className="font-mono-alt text-[11px] text-[#1F5EFF]">Questions &amp; Quotes</span>
      </div>
      <h3 className="font-display font-light text-3xl md:text-4xl leading-tight tracking-[-0.01em]">
        Send us a message — <span className="text-[#1F5EFF]">we reply within 2 business hours.</span>
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
        <input value={f.name} onChange={set("name")} placeholder="Your name *" className={finp} data-testid="contact-form-name" maxLength={120} />
        <input value={f.phone} onChange={set("phone")} placeholder="Phone *" className={finp} data-testid="contact-form-phone" maxLength={40} />
      </div>
      <input value={f.email} onChange={set("email")} type="email" placeholder="Email *" className={finp} data-testid="contact-form-email" />
      <textarea value={f.message} onChange={set("message")} rows={4} placeholder="How can we help? (vehicle, service, question…) *" className={finp + " resize-y"} data-testid="contact-form-message" maxLength={2000} />
      {error && <div className="text-red-300 text-sm" data-testid="contact-form-error">{error}</div>}
      <div>
        <button
          type="submit"
          disabled={busy}
          className="inline-flex items-center gap-2.5 px-6 py-3 rounded-full bg-[#1F5EFF] text-white text-sm font-medium hover:bg-white hover:text-black transition-colors disabled:opacity-40"
          data-testid="contact-form-submit"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Send Message
        </button>
      </div>
    </form>
  );
}

export default function Contact() {
  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-36 border-t border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-8 h-px bg-[#1F5EFF]" />
          <span className="font-mono-alt text-[12px] text-[#1F5EFF]">
            Visit The Studio
          </span>
        </div>
        <h2 className="font-display text-4xl md:text-6xl font-light tracking-tight text-white max-w-3xl leading-[1.02]">
          Edmonton, Alberta.{" "}
          <span className="text-white/50">By appointment.</span>
        </h2>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5 space-y-4"
          >
            {[
              {
                icon: Phone,
                label: "Phone",
                value: CONTACT.phone,
                href: `tel:${CONTACT.phone.replace(/[^\d+]/g, "")}`,
                tid: "contact-phone",
              },
              {
                icon: Mail,
                label: "Email",
                value: CONTACT.email,
                href: `mailto:${CONTACT.email}`,
                tid: "contact-email",
              },
              {
                icon: MapPin,
                label: "Address",
                value: CONTACT.address,
                href: `https://maps.google.com/?q=${encodeURIComponent(CONTACT.address)}`,
                tid: "contact-address",
              },
            ].map((it, i) => {
              const Icon = it.icon;
              return (
                <a
                  key={i}
                  href={it.href}
                  target={it.label === "Address" ? "_blank" : undefined}
                  rel="noreferrer"
                  className="group block bg-[#17171c] border border-white/12 rounded-2xl p-6 hover:border-[#1F5EFF]/40 transition-colors"
                  data-testid={it.tid}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-[#1F5EFF] group-hover:bg-[#1F5EFF] group-hover:text-white transition-all duration-500">
                      <Icon className="w-5 h-5" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1">
                      <div className="font-mono-alt text-[10px] text-white/40">
                        {it.label}
                      </div>
                      <div className="mt-1 text-white text-lg font-display">
                        {it.value}
                      </div>
                    </div>
                  </div>
                </a>
              );
            })}

            <div className="bg-[#17171c] border border-white/12 rounded-2xl p-6" data-testid="contact-hours">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-[#1F5EFF]">
                  <Clock className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <div className="font-mono-alt text-[10px] text-white/40 mb-3">
                    Hours
                  </div>
                  <div className="space-y-2">
                    {CONTACT.hours.map((h, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between text-sm"
                      >
                        <span className="text-white/70">{h.day}</span>
                        <span className="text-white font-mono-alt text-[11px]">
                          {h.time}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              {CONTACT.socials.map((s) => (
                <a
                  key={s.name}
                  href={s.url}
                  data-testid={`social-${s.name.toLowerCase()}`}
                  className="flex-1 text-center bg-[#17171c] border border-white/12 rounded-xl py-3 text-white/70 hover:text-white hover:border-[#1F5EFF]/40 transition-colors"
                >
                  <div className="font-mono-alt text-[10px] text-white/40">
                    {s.name}
                  </div>
                  <div className="text-sm mt-1">{s.handle}</div>
                </a>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-7 rounded-2xl border border-white/10 overflow-hidden bg-[#17171c] p-8 md:p-12 flex flex-col justify-center"
            data-testid="contact-form-panel"
          >
            <ContactForm />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
